import { useState } from "react";
import { ERROR_MESSAGES } from "../../constant/constant.js";

/**
 * @typedef {Object} RegisterData
 * @property {string} firstName - First name of the user
 * @property {string} lastName - Last name of the user
 * @property {string} email - Email of the user
 * @property {string} password - Password of the user
 */
/**
 * @typedef {import("./useGoogleLogin.js").GoogleLoginResponse LoginResponse}
 */

/**
 * Custom hook to register a new user
 */
export const useRegisterUser = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  /**
   * Registers a new user
   * @param {RegisterData} registerData - The user registration data
   * @param {(data: LoginResponse) => void} [onSuccess] - Callback function to be called on success
   */
  const registerUser = async (registerData, onSuccess) => {
    setLoading(true);
    setError("");
    setData(null);

    try {
      const response = await fetch(`${apiUrl}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(registerData),
      });

      if (!response.ok) {
        let errorMsg = ERROR_MESSAGES?.UNKNOWN || "Something went wrong";
        try {
          const errorData = await response.json();
          if (errorData?.errors) {
            const errorKey = Object.keys(errorData.errors)[0];
            errorMsg = errorData.errors[errorKey][0];
          } else {
            errorMsg = errorData?.message || errorMsg;
          }
        } catch {
          if (response.status === 500) {
            errorMsg =
              ERROR_MESSAGES?.INTERNAL_SERVER || "Internal server error";
          }
        }
        throw new Error(errorMsg);
      }
      /**
       * @type {LoginResponse} responseData
       */
      const responseData = await response.json();
      setData(responseData);

      if (onSuccess) {
        onSuccess(responseData);
      }

      return responseData;
    } catch (err) {
      setError(
        err?.message || ERROR_MESSAGES?.UNKNOWN || "Something went wrong",
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    registerUser,
    loading,
    error,
    data,
    setError,
  };
};
